import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Workspace } from 'sdk';
import {
  type CreateWorkspacesData,
  createWorkspaces,
  type DeleteWorkspacesData,
  deleteWorkspaces,
  type GetWorkspacesData,
  getWorkspace,
  getWorkspaces,
  type UpdateWorkspaceData,
  updateWorkspace,
} from 'sdk';
import { appConfig } from 'shared';
import { ApiError } from '~/lib/api';
import { addMyMembershipCache, getApiIncludedMembership } from '~/modules/memberships/query-mutations';
import { cacheCreate, cacheRemove, cacheUpdate, removeDetailQueriesOnceUnobserved } from '~/query/basic/cache-mutations';
import { createEntityKeys } from '~/query/basic/create-query-keys';
import { registerEntityQueryKeys } from '~/query/basic/entity-query-registry';
import { createCacheFinder } from '~/query/basic/find-in-list-cache';
import { defaultOrderFor } from '~/query/basic/get-query-key-sort-order';
import { offsetPaging, pageQuery, refetchOnMountForIncluded } from '~/query/basic/infinite-query-options';
import { invalidateIfLastMutation } from '~/query/basic/invalidation-helpers';
import { preserveIncluded } from '~/query/basic/preserve-included';
import type { MutationData } from '~/query/types';

type WorkspaceFilters = Omit<GetWorkspacesData['query'], 'limit' | 'offset'>;

const keys = createEntityKeys<WorkspaceFilters>('workspace');

// Register query keys for dynamic lookup in stream handlers
registerEntityQueryKeys('workspace', keys);

/** Defines React Query cache keys for workspace. */
export const workspaceQueryKeys = keys;

const findWorkspaceInCache = createCacheFinder<Workspace>('workspace');

/** Find a workspace in cache by id or slug. Slug matches are scoped to the given tenant. */
export const findWorkspaceByIdOrSlug = (idOrSlug: string, tenantId: string): Workspace | undefined =>
  findWorkspaceInCache((ws) => ws.id === idOrSlug || (ws.slug === idOrSlug && ws.tenantId === tenantId));

type WorkspacesListParams = Omit<NonNullable<GetWorkspacesData['query']>, 'limit' | 'offset'> & { limit?: number };

/** Paginated workspaces infinite query. `include` is deliberately not part of the cache key. */
export const workspacesListQueryOptions = (params: WorkspacesListParams = {}) => {
  const {
    q = '',
    sort = 'displayOrder',
    order = defaultOrderFor(sort),
    organizationId,
    role,
    excludeArchived,
    include,
    limit = appConfig.requestLimits.default,
  } = params;

  const filters = { q, sort, order, organizationId, role, excludeArchived };
  return infiniteQueryOptions({
    queryKey: keys.list.filtered(filters),
    ...offsetPaging(limit, (offset, signal) => getWorkspaces({ query: { ...filters, include, ...pageQuery(limit, offset) }, signal })),
    refetchOnMount: refetchOnMountForIncluded(include),
  });
};

export const workspaceQueryOptions = (id: string, organizationId: string, tenantId: string) =>
  queryOptions({
    queryKey: keys.detail.byId(id),
    queryFn: () => getWorkspace({ path: { id, organizationId, tenantId } }),
    placeholderData: () => findWorkspaceByIdOrSlug(id, tenantId),
    structuralSharing: preserveIncluded,
  });

export const useWorkspaceCreateMutation = () => {
  const queryClient = useQueryClient();
  const listKey = keys.list.base;

  return useMutation<Workspace, ApiError, MutationData<CreateWorkspacesData>>({
    mutationKey: keys.create,
    mutationFn: async ({ path, body }) => {
      const result = await createWorkspaces({ path, body });
      if (!result.data.length) throw new ApiError({ status: 422, type: 'create_resource', entityType: 'workspace', severity: 'error' });
      return result.data[0] as Workspace;
    },
    onSuccess: (createdWorkspace) => {
      const membership = getApiIncludedMembership(createdWorkspace);
      if (membership) addMyMembershipCache(membership);
      cacheCreate(listKey, [createdWorkspace]);
    },
    onSettled: () => {
      invalidateIfLastMutation(queryClient, keys.all, listKey);
    },
  });
};

export const useUpdateWorkspaceMutation = () => {
  const queryClient = useQueryClient();
  const listKey = keys.list.base;

  return useMutation<Workspace, ApiError, MutationData<UpdateWorkspaceData>>({
    mutationKey: keys.update,
    mutationFn: ({ path, body }) => updateWorkspace({ path, body }),
    onSuccess: (updatedWorkspace) => {
      cacheUpdate(listKey, [updatedWorkspace]);
      queryClient.invalidateQueries({ queryKey: keys.detail.base });
      // Directly update detail cache so beforeLoad doesn't use stale slug for URL rewrite
      queryClient.setQueryData(keys.detail.byId(updatedWorkspace.id), updatedWorkspace);
    },
    onSettled: () => {
      invalidateIfLastMutation(queryClient, keys.all, listKey);
    },
    gcTime: 1000 * 10,
  });
};

export const useWorkspaceDeleteMutation = () => {
  const queryClient = useQueryClient();
  const listKey = keys.list.base;

  return useMutation<void, ApiError, MutationData<DeleteWorkspacesData> & { workspaces: Workspace[] }>({
    mutationKey: keys.delete,
    mutationFn: async ({ path, body }) => {
      await deleteWorkspaces({ path, body });
    },
    onSuccess: (_, { workspaces }) => {
      cacheRemove(listKey, workspaces);
      removeDetailQueriesOnceUnobserved(
        queryClient,
        keys.detail.base,
        workspaces.map(({ id }) => id),
      );
    },
    onSettled: () => {
      invalidateIfLastMutation(queryClient, keys.all, listKey);
    },
  });
};
